import type { PlatformAdapter } from './PlatformAdapter';
import type { Conversation, Message } from '@/lib/types';

// Front SDK is available globally or via import
// @ts-ignore - Front SDK types
import Front from '@frontapp/plugin-sdk';

/**
 * Front platform adapter
 * Integrates with Front's plugin SDK
 */
export class FrontAdapter implements PlatformAdapter {
  private conversationCallback: ((conversation: Conversation) => void) | null = null;
  private teammateCallback: ((teammate: { id: string; email: string; name: string }) => void) | null = null;
  private subscriptionCount = 0;
  private unsubscribe: (() => void) | null = null;
  private lastConversationId: string | null = null;
  private contextUpdateCount = 0;
  private currentDraftId: string | null = null;

  async initialize(): Promise<void> {
    console.log('[FrontAdapter] initialize() called, current subscription count:', this.subscriptionCount);
    console.log('[FrontAdapter] Checking Front SDK availability...');
    console.log('[FrontAdapter] typeof Front:', typeof Front);
    console.log('[FrontAdapter] Front object:', Front);
    console.log('[FrontAdapter] window.Front:', (window as any).Front);

    // Check if Front SDK is available
    if (typeof Front === 'undefined' || !Front.contextUpdates) {
      console.warn('[FrontAdapter] Front SDK not available');
      console.warn('[FrontAdapter] Front is undefined:', typeof Front === 'undefined');
      console.warn('[FrontAdapter] Front.contextUpdates missing:', Front && !Front.contextUpdates);

      // In development mode, auto-enable mock data for easier testing
      const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development';
      const mockDataEnabled = import.meta.env.VITE_ENABLE_MOCK_DATA === 'true' || isDev;

      if (mockDataEnabled) {
        console.log('[FrontAdapter] Mock data enabled, using dummy conversation');

        // Provide mock teammate data
        if (this.teammateCallback) {
          const mockTeammate = {
            id: 'mock_teammate_123',
            email: 'demo@crewflow.com',
            name: 'Demo User',
          };
          console.log('[FrontAdapter] Providing mock teammate data:', mockTeammate);
          this.teammateCallback(mockTeammate);
        }

        setTimeout(() => {
          const mockConv = this.getMockConversation();
          if (this.conversationCallback) {
            this.conversationCallback(mockConv);
          }
        }, 100);
        return; // Success with mock data
      }

      // If not in dev and mock data not enabled, throw error
      throw new Error(
        'Front SDK not available. Running outside Front? Enable mock data by setting VITE_ENABLE_MOCK_DATA=true in .env'
      );
    }

    console.log('[FrontAdapter] Front SDK loaded, subscribing to context updates');
    console.log('[FrontAdapter] Available Front methods:', Object.keys(Front));

    // IMPORTANT: Unsubscribe from previous subscription to prevent duplicates
    if (this.unsubscribe) {
      console.warn('[FrontAdapter] WARNING: Unsubscribing from previous subscription to prevent duplicates!');
      this.unsubscribe();
      this.subscriptionCount--;
    }

    // Note: Account authentication is handled in App.tsx via /api/v1/plugin/session
    // Front SDK provides conversation context, backend validates account access

    // Fetch initial context immediately (don't wait for first update)
    try {
      console.log('[FrontAdapter] Fetching initial context...');
      const initialContext = await Front.getContext();
      console.log('[FrontAdapter] Initial context received:', initialContext);

      // Extract teammate info immediately
      if (initialContext && initialContext.teammate && this.teammateCallback) {
        const teammate = {
          id: initialContext.teammate.id || '',
          email: initialContext.teammate.email || '',
          name: initialContext.teammate.name || initialContext.teammate.username || '',
        };
        console.log('[FrontAdapter] Initial teammate info:', teammate);
        this.teammateCallback(teammate);
      }
    } catch (error) {
      console.error('[FrontAdapter] Failed to get initial context:', error);
      // Continue anyway - will get it from contextUpdates
    }

    // Subscribe to context updates (Front's observable pattern)
    const subscription = Front.contextUpdates.subscribe(async (context: any) => {
      this.contextUpdateCount++;
      console.log(`[FrontAdapter] Front context update #${this.contextUpdateCount} received`);

      // Extract teammate information (for seat management)
      if (context && context.teammate && this.teammateCallback) {
        const teammate = {
          id: context.teammate.id || '',
          email: context.teammate.email || '',
          name: context.teammate.name || context.teammate.username || '',
        };
        console.log('[FrontAdapter] Teammate info:', teammate);
        this.teammateCallback(teammate);
      }

      if (context && context.conversation) {
        const conversationId = context.conversation.id;
        console.log('[FrontAdapter] Conversation ID:', conversationId);

        // Store draft ID if available
        if (context.conversation.draftId) {
          this.currentDraftId = context.conversation.draftId;
          console.log('[FrontAdapter] Draft ID:', this.currentDraftId);
        }

        // IMPORTANT: Only process if conversation actually changed
        if (conversationId === this.lastConversationId) {
          console.log('[FrontAdapter] Same conversation as last update, skipping parse to prevent loop');
          return;
        }

        this.lastConversationId = conversationId;
        console.log('[FrontAdapter] New conversation detected, parsing...');

        // Front Plugin SDK provides Front.listMessages() for partial message history (3-20 messages)
        // Backend will automatically fetch full message thread via Front Core API in production
        // when FRONT_API_TOKEN is configured

        const conversation = await this.parseConversation(context.conversation);
        if (conversation && this.conversationCallback) {
          console.log('[FrontAdapter] Firing conversationCallback for conversation:', conversationId);
          this.conversationCallback(conversation);
        }
      }
    });

    // Store unsubscribe function
    this.unsubscribe = () => {
      console.log('[FrontAdapter] Unsubscribing from Front.contextUpdates');
      subscription.unsubscribe();
    };

    this.subscriptionCount++;
    console.log('[FrontAdapter] Subscription created, new count:', this.subscriptionCount);
  }

  async getCurrentConversation(): Promise<Conversation | null> {
    // Front SDK uses observables, not a fetch method
    // The conversation is delivered via contextUpdates subscription
    // Return null here, actual data comes through the subscription
    return null;
  }

  onConversationChange(callback: (conversation: Conversation) => void): void {
    this.conversationCallback = callback;
  }

  onTeammateChange(callback: (teammate: { id: string; email: string; name: string }) => void): void {
    this.teammateCallback = callback;
  }

  onTokenChange(callback: (token: string) => void): void {
    this.tokenCallback = callback;

    // Get token from Front SDK immediately
    // The token is provided by Front's plugin context and should be sent with all API requests
    if (typeof Front !== 'undefined' && Front.getContext) {
      Front.getContext().then((context: any) => {
        if (context && context.token) {
          console.log('[FrontAdapter] Got plugin token from Front SDK');
          callback(context.token);
        } else {
          console.warn('[FrontAdapter] No token in Front context:', context);
        }
      }).catch((error: any) => {
        console.error('[FrontAdapter] Failed to get Front context:', error);
      });
    } else {
      console.warn('[FrontAdapter] Front.getContext not available');
    }
  }

  async insertDraft(conversationId: string, draft: string): Promise<void> {
    if (typeof Front === 'undefined' || !Front.createDraft) {
      console.log('Mock: Insert draft into conversation', conversationId, draft);
      // In development mode, just log - the Sidebar component will show the toast
      return;
    }

    try {
      // @ts-ignore - Front SDK types may not match exactly
      await Front.createDraft({ body: draft });
    } catch (error) {
      console.error('Failed to insert draft:', error);
      throw error;
    }
  }

  async fetchDraft(): Promise<string | null> {
    // @ts-ignore - Front SDK types may not include fetchDraft yet
    if (typeof Front === 'undefined' || !Front.fetchDraft) {
      console.log('Mock: Fetch draft from Front (not available in development)');
      return null;
    }

    if (!this.currentDraftId) {
      console.log('No draft ID available from context');
      return null;
    }

    try {
      // Fetch the specific draft using the ID from context
      // @ts-ignore - Front SDK types may not match exactly
      const draft = await Front.fetchDraft(this.currentDraftId);
      console.log('Fetched draft from Front:', draft);

      // Draft structure: { id, content: { body, type }, isEditable }
      if (draft && draft.content && draft.content.body) {
        return draft.content.body;
      }

      return null;
    } catch (error) {
      console.error('Failed to fetch draft from Front:', error);
      // If no draft exists, Front SDK may throw an error
      return null;
    }
  }

  getPlatformName(): string {
    return 'front';
  }


  private async parseConversation(data: any): Promise<Conversation | null> {
    if (!data) {
      return null;
    }

    console.log('Parsing Front conversation:', {
      id: data.id,
      subject: data.subject,
      blurb: data.blurb,
      recipient: data.recipient,
      hasBlurb: !!data.blurb,
    });

    // Try to fetch message history via Front Plugin SDK
    // Note: This returns 3-20 messages (partial history, not complete thread)
    // In production, backend fetches full history via Front Core API with OAuth
    // This improves local development experience without requiring OAuth setup
    let messages: Message[] = [];

    try {
      // @ts-ignore - Front.listMessages() exists but is not in type definitions
      if (typeof Front !== 'undefined' && Front.listMessages) {
        console.log('Attempting to fetch message history via Front.listMessages()');
        // @ts-ignore - Front.listMessages() exists but is not in type definitions
        const messagesResponse = await Front.listMessages();

        console.log('Front.listMessages() response:', messagesResponse);
        console.log('Raw messages from Front SDK:');
        if (messagesResponse && messagesResponse.results) {
          messagesResponse.results.forEach((msg: any, i: number) => {
            console.log(`  Message ${i + 1}:`, {
              id: msg.id,
              body: msg.body,
              blurb: msg.blurb,
              text: msg.text,
              author: msg.author,
              is_inbound: msg.is_inbound,
              created_at: msg.created_at,
            });
          });
        }

        if (messagesResponse && messagesResponse.results && Array.isArray(messagesResponse.results)) {
          messages = this.parseMessages(messagesResponse.results);
          console.log(`Parsed ${messages.length} messages from Front SDK`);
        }
      }
    } catch (error) {
      console.warn('Failed to fetch messages via Front.listMessages(), falling back to blurb:', error);
    }

    // Fallback to blurb if no messages were fetched OR if messages have no content
    if (messages.length === 0 || messages.every(m => !m.body || m.body.trim() === '')) {
      console.log('No message content from listMessages(), using conversation blurb as fallback');
      const messageBody = data.blurb || 'No preview available - connect Front via OAuth for full message content';

      messages = [{
        id: `${data.id}_preview`,
        from: data.recipient?.handle || data.recipient?.contact?.handles?.[0]?.handle || 'unknown',
        from_name: data.recipient?.name || 'Unknown',
        body: messageBody,
        timestamp: new Date(),
        direction: 'inbound',
        is_from_customer: true,
      }];
    }

    // Check if there are any inbound customer messages
    // Skip analysis for outbound-only conversations (e.g., when composing a new email)
    const hasInboundMessages = messages.some(msg => msg.is_from_customer);
    if (!hasInboundMessages) {
      console.log('[FrontAdapter] Skipping conversation - no inbound customer messages (outbound-only)');
      return null;
    }

    // Check if the most recent message is outbound (we just replied)
    // Skip analysis since we've already responded and don't need to draft again
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && !lastMessage.is_from_customer) {
      console.log('[FrontAdapter] Skipping conversation - most recent message is outbound (we already replied)');
      return null;
    }

    const conversation: Conversation = {
      id: data.id,
      subject: data.subject || '(No Subject)',
      messages,
      customer_email: data.recipient?.handle || data.recipient?.contact?.handles?.[0]?.handle || 'unknown@example.com',
      customer_name: data.recipient?.name,
      assigned_to: data.assignee?.email,
      status: this.mapStatus(data.status),
      priority: 'normal' as const,
      last_message_at: messages.length > 0 ? messages[messages.length - 1].timestamp : new Date(),
    };

    console.log('[FrontAdapter] ===== PARSED CONVERSATION =====');
    console.log('[FrontAdapter] Conversation ID:', conversation.id);
    console.log('[FrontAdapter] Subject:', conversation.subject);
    console.log('[FrontAdapter] Number of messages:', messages.length);
    console.log('[FrontAdapter] Message content:');
    messages.forEach((msg, i) => {
      console.log(`  [${i + 1}] ${msg.direction} from ${msg.from_name}: "${msg.body.substring(0, 100)}..."`);
    });
    console.log('[FrontAdapter] ================================');

    return conversation;
  }

  /**
   * Parse Front SDK messages into our internal Message format
   * Front message structure: { id, body, blurb, author, created_at, is_inbound, ... }
   */
  private parseMessages(frontMessages: any[]): Message[] {
    return frontMessages.map((msg) => {
      // Extract author info
      const authorHandle = msg.author?.handle || msg.author?.email || 'unknown';
      const authorName = msg.author?.name || msg.author?.handle || 'Unknown';

      // Parse timestamp (Front returns Unix timestamp in seconds)
      const timestamp = msg.created_at
        ? new Date(msg.created_at * 1000)
        : new Date();

      // Determine direction and if from customer
      // is_inbound means message came from customer
      const isInbound = msg.is_inbound === true;
      const direction: 'inbound' | 'outbound' = isInbound ? 'inbound' : 'outbound';

      return {
        id: msg.id || `msg_${Date.now()}_${Math.random()}`,
        from: authorHandle,
        from_name: authorName,
        body: msg.body || msg.blurb || '',
        body_html: msg.body_html,
        timestamp,
        direction,
        is_from_customer: isInbound,
      };
    }).sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime()); // Sort oldest to newest
  }

  private mapStatus(frontStatus: string): 'open' | 'closed' | 'snoozed' {
    switch (frontStatus) {
      case 'archived':
      case 'deleted':
        return 'closed';
      case 'snoozed':
        return 'snoozed';
      default:
        return 'open';
    }
  }

  private getMockConversation(): Conversation {
    // Return a random mock conversation for testing different sentiments
    const mockConversations = [
      {
        id: 'mock-conv-1',
        subject: 'Help with billing',
        messages: [{
          id: 'msg-1',
          from: 'customer@example.com',
          from_name: 'John Doe',
          body: "Hi, I was charged twice for my subscription. Can you help?",
          timestamp: new Date(Date.now() - 3600000),
          direction: 'inbound' as const,
          is_from_customer: true,
        }],
      },
      {
        id: 'mock-conv-2',
        subject: 'Thank you for the help!',
        messages: [{
          id: 'msg-2',
          from: 'happy@example.com',
          from_name: 'Jane Smith',
          body: "Thank you so much for resolving my issue quickly! Your support team is amazing.",
          timestamp: new Date(Date.now() - 1800000),
          direction: 'inbound' as const,
          is_from_customer: true,
        }],
      },
      {
        id: 'mock-conv-3',
        subject: 'Quick question about features',
        messages: [{
          id: 'msg-3',
          from: 'curious@example.com',
          from_name: 'Bob Johnson',
          body: "Hey, I'm curious about the new dashboard feature. How do I enable it?",
          timestamp: new Date(Date.now() - 7200000),
          direction: 'inbound' as const,
          is_from_customer: true,
        }],
      },
    ];

    // Pick a random mock conversation
    const mock = mockConversations[Math.floor(Math.random() * mockConversations.length)];

    return {
      id: mock.id,
      subject: mock.subject,
      messages: mock.messages,
      customer_email: mock.messages[0].from,
      customer_name: mock.messages[0].from_name,
      status: 'open',
      priority: 'normal',
      last_message_at: mock.messages[0].timestamp,
    };
  }
}
